import { Portal } from "@/utils/components/Portal";
import { create } from "zustand";
import { button, folder, useControls } from "leva";

type ErrorState = {
  isActive: boolean;
  isLeaving: boolean;
  message: string;
  setError: (message: string) => void;
  startDismiss: () => void;
  clearError: () => void;
};

export const useErrorStore = create<ErrorState>((set) => ({
  isActive: false,
  isLeaving: false,
  message: "",
  setError: (message) => set({ message, isActive: true, isLeaving: false }),
  startDismiss: () => set((s) => ({ ...s, isLeaving: true })),
  clearError: () => set({ message: "", isActive: false, isLeaving: false }),
}));

let dismissTimeout: ReturnType<typeof setTimeout> | null = null;
let clearTimeoutId: ReturnType<typeof setTimeout> | null = null;

export function useError() {
  const setError = useErrorStore((s) => s.setError);
  const startDismiss = useErrorStore((s) => s.startDismiss);
  const clearError = useErrorStore((s) => s.clearError);

  const toggleError = (message: string, duration = 2000) => {
    if (dismissTimeout) {
      clearTimeout(dismissTimeout);
      dismissTimeout = null;
    }
    if (clearTimeoutId) {
      clearTimeout(clearTimeoutId);
      clearTimeoutId = null;
    }

    setError(message.toString());

    if (duration > 0) {
      dismissTimeout = setTimeout(() => {
        startDismiss();
        clearTimeoutId = setTimeout(() => {
          clearError();
          clearTimeoutId = null;
        }, 260);
        dismissTimeout = null;
      }, duration);
    }
  };

  return { toggleError };
}

export function ErrorBanner() {
  const isActive = useErrorStore((s) => s.isActive);
  const isLeaving = useErrorStore((s) => s.isLeaving);
  const message = useErrorStore((s) => s.message);
  const startDismiss = useErrorStore((s) => s.startDismiss);
  const clearError = useErrorStore((s) => s.clearError);
  const { toggleError } = useError();

  const handleDismiss = () => {
    startDismiss();
    setTimeout(() => clearError(), 260);
  };

  useControls(
    "UI",
    () => ({
      global: folder(
        {
          "toggle error": button(() => toggleError("something went wrong", 2500)),
        },
        { collapsed: true }
      ),
    }),
    [toggleError]
  );

  if (!isActive || !message) return null;

  return (
    <Portal>
      <div className="pointer-events-none fixed top-1 left-0 right-0 z-[9999] w-full px-2">
        <div className={`pointer-events-auto flex min-h-14 items-center justify-between border border-red-500 bg-primary/95 px-6 py-3 shadow-lg backdrop-blur-md ${isLeaving ? "animate-error-out" : "animate-error-in"}`}>
          <div className="flex items-center gap-3">
            <div className="h-2.5 w-2.5 shrink-0 rounded-full bg-red-400" />
            <p className="text-lg font-semibold tracking-wide text-red-500">{message}</p>
          </div>
          <button
            onClick={handleDismiss}
            className="ml-4 shrink-0 text-tertiary hover:text-red-400 transition-colors"
            aria-label="Dismiss error"
          >
            ✕
          </button>
        </div>
      </div>
    </Portal>
  );
}
